
import { computed, defineComponent, onMounted, reactive, toRaw, watch } from 'vue'

// components
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'

// composable
import useHelpers from '@/common/composable/useHelpers'

// types
import { PeriodT } from '@/core/types/create_order.types'

// classes
import { DataState } from '@/core/classes/DataState.class'
import { orders } from '@/core/realisations/orders.realisation'

import { formatDateFromBackend } from '@/common/helpers/formatDate'

export default defineComponent({
    components: {
        TheInput,
        TheSelect,
        TheButton,
        Checkbox,
    },
    props: {
        opened: {
            type: Boolean,
            default: false,
        },
        stepNumber: {
            type: Number,
            default: 0,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        period: {
            type: Number,
            default: null,
        },
        date: {
            type: String,
            default: null,
        },
    },
    emits: ['submit', 'reset'],
    setup(props, { emit }) {
        const { findObjectByValue } = useHelpers()

        const state = reactive({
            is_selected: false,
            is_period_check: false,
            date: null as string | null,
            period_id: 3,
            period: null as PeriodT | null,
        })

        const data_state = reactive({
            periods: new DataState<PeriodT>({}),
        })

        const min_date = computed(() => {
            const date = new Date(Date.now())
            const hours = date.getHours()

            if (hours >= 14) {
                date.setDate(date.getDate() + 1)
            }

            return date.toISOString().split('T')[0]
        })

        const onPeriodSelected = (period_id: number) => {
            const period = findObjectByValue('id', period_id, data_state.periods.list)

            if (!period) return
            state.period = period
        }

        const isTodayAfternoon = computed(() => {
            const current_date = new Date(),
                current_hour = current_date.getHours()
            const current_date_iso = current_date.toISOString().split('T')[0]

            return current_hour >= 10 && state.date === current_date_iso
        })

        const setPeriod = () => {
            if (isTodayAfternoon.value) {
                state.period_id = 2
            } else {
                state.period_id = 3
            }

            const period = findObjectByValue('id', state.period_id, data_state.periods.list)
            state.period = period || null
        }

        const onDateSelected = () => {
            state.is_period_check = false
            setPeriod()
        }

        const emitSubmit = () => {
            if (!state.period) return

            state.is_selected = true

            emit(
                'submit',
                toRaw({
                    date: state.date,
                    period_id: state.period_id,
                    period: state.period.title,
                })
            )
        }

        const emitReset = () => {
            state.is_selected = false
            state.date = null
            state.period_id = 3

            const period = findObjectByValue('id', state.period_id, data_state.periods.list)
            state.period = period || null

            emit('reset')
        }

        const getPeriods = computed(() => {
            if (isTodayAfternoon.value) {
                return data_state.periods.list.filter((period) => period.id === 2)
            }

            return data_state.periods.list
        })

        const loadPeriods = async () => {
            data_state.periods.pagination.current_page = 1

            try {
                data_state.periods.is_loading = true
                const response = await orders.getPeriods()
                data_state.periods.list = response.data

                data_state.periods.pagination.total_pages = 1

                data_state.periods.is_failed = false
                data_state.periods.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.periods.failure.catchError(error)

                if (!data_state.periods.failure.error_state.is_cancel) {
                    data_state.periods.is_failed = true
                }
            } finally {
                if (!data_state.periods.failure.error_state.is_cancel) {
                    data_state.periods.is_loading = false
                }
            }
        }

        const onCheckboxChange = (value: boolean) => {
            if (!value) {
                setPeriod()
            }
        }

        watch(
            () => props.opened,
            (opened) => {
                if (!opened) {
                    state.is_selected = false
                    state.date = null
                    state.period_id = 3

                    const period = findObjectByValue('id', state.period_id, data_state.periods.list)
                    state.period = period || null
                }
            }
        )

        watch(
            () => props.date,
            (date) => {
                if (date) {
                    state.date = date

                    if (state.period_id) {
                        state.is_selected = true
                    }
                }
            }
        )

        watch(
            () => props.period,
            (period_id) => {
                if (period_id) {
                    state.period_id = period_id

                    const period = findObjectByValue('id', period_id, data_state.periods.list)

                    if (!period) return
                    state.period = period

                    if (state.date) {
                        state.is_selected = true
                    }
                }
            }
        )

        onMounted(loadPeriods)

        const maxDate = computed(() => {
            const date = new Date()
            date.setFullYear(date.getFullYear() + 1)
            return date.toISOString().split('T')[0]
        })

        return {
            state,
            data_state,
            min_date,
            emitSubmit,
            emitReset,
            onPeriodSelected,
            onDateSelected,
            loadPeriods,
            onCheckboxChange,
            //
            getPeriods,
            formatDateFromBackend,
            maxDate
        }
    },
})
